// GUIDED-FLOW: the guided-flow reads (`flows.*`) and the pieces the panel, the banner, the chat
// card and the seat actions share. Keys live under ["company", co] so a company-wide refresh
// (a seat save, a new seat) refetches them too.
import type { Answers, AnswerValue, FlowDef, Viewer } from "@companyos/domain-work/flows";
import { ApiError, type OperationOutput } from "@companyos/sdk";
import { queryOptions } from "@tanstack/react-query";
import type { Api } from "../../api/client";

export type FlowRunView = OperationOutput<"flows.get">;
export type FlowRunSummary = OperationOutput<"flows.list">["data"][number];
export type FlowMissing = FlowRunView["missing"][number];
export type FlowHelperAnswer = OperationOutput<"flows.helper">;

export const flowKeys = {
  all: (co: string) => ["company", co, "flows"] as const,
  run: (co: string, id: string) => ["company", co, "flows", "run", id] as const,
  mineOpen: (co: string) => ["company", co, "flows", "mine-open"] as const,
  seatOpen: (co: string, seat: string) => ["company", co, "flows", "seat-open", seat] as const,
};

export const flowQuery = (api: Api, co: string, id: string) =>
  queryOptions({
    queryKey: flowKeys.run(co, id),
    queryFn: () => api.client.flows.get({ params: { co, id } }),
    retry: false,
  });

/** The caller's own open runs (the shell's banner). */
export const myOpenFlowsQuery = (api: Api, co: string) =>
  queryOptions({
    queryKey: flowKeys.mineOpen(co),
    queryFn: () =>
      api.client.flows.list({ params: { co }, query: { mine: "true", status: "open" } }),
    staleTime: 60_000,
  });

/** A seat's open runs (the Seat setup modal's "Open seat setup"). */
export const seatOpenFlowsQuery = (api: Api, co: string, seat: string) =>
  queryOptions({
    queryKey: flowKeys.seatOpen(co, seat),
    queryFn: () =>
      api.client.flows.list({ params: { co }, query: { seat_id: seat, status: "open" } }),
  });

/** The engine's viewer from the run view. */
export function viewerOf(view: FlowRunView): Viewer {
  return {
    audiences: new Set(view.viewer.audiences),
    assigneeAccess: view.viewer.assignee_access,
  };
}

/** The answers as the form shows them: saved, then template suggestions, then the draft. */
export function mergedAnswers(view: FlowRunView, draft: Answers): Answers {
  const out: Record<string, AnswerValue> = {};
  for (const [k, v] of Object.entries(view.prefill)) out[k] = v;
  for (const [k, v] of Object.entries(view.answers))
    if (v !== null || out[k] === undefined) out[k] = v;
  return { ...out, ...draft };
}

/** A real field's column, as a server error may name it, to the flow field that writes it. */
const COLUMN_FIELD: Record<string, string> = {
  supervisor_seat_id: "reports_to",
  role: "role_id",
  roles: "access_level",
  require_approval: "post_approval",
};

/**
 * A refused save's errors by field key: `values.title` → `title`, a column the field writes
 * (`supervisor_seat_id` → `reports_to`) to that field when the step has it. Anything else keeps
 * its path (`step`, …) for the step's summary.
 */
export function fieldErrorsOf(
  error: unknown,
  step?: { fields: readonly { key: string }[] },
): Record<string, string> {
  if (!(error instanceof ApiError)) return {};
  const has = (k: string) => step?.fields.some((f) => f.key === k) ?? false;
  const out: Record<string, string> = {};
  for (const e of error.problem?.errors ?? []) {
    const raw = e.path.replace(/^values\./, "");
    const alias = COLUMN_FIELD[raw];
    const key = has(raw) ? raw : alias && has(alias) ? alias : raw;
    out[key] = out[key] ? `${out[key]}; ${e.message}` : e.message;
  }
  return out;
}

/**
 * A refused submit: 422 `validation_failed` with `required` (and `invalid_value`) errors, path
 * `<step>.<field>`. The panel then refetches the run and shows its `missing` list.
 */
export function submitRefused(error: unknown): boolean {
  return error instanceof ApiError && error.status === 422 && error.code === "validation_failed";
}

/** `flows.send` refused because one is open on the seat (409 `conflict`): that run's id. */
export function openRunOf(error: unknown): string | null {
  if (!(error instanceof ApiError) || error.status !== 409) return null;
  const current = error.problem?.current as { id?: unknown } | undefined;
  return typeof current?.id === "string" ? current.id : null;
}

/**
 * A refused submit's errors other than a missing required field (that list comes from the
 * re-read run): `invalid_value`, `invalid_reference` and any other code, each message once.
 */
export function submitIssuesOf(error: unknown): string[] {
  if (!submitRefused(error)) return [];
  const list = ((error as ApiError).problem?.errors ?? [])
    .filter((e) => e.code !== "required")
    .map((e) => e.message);
  return [...new Set(list)];
}

/**
 * Whether a submit error shows as the general error: every refusal that is not a 422 (409
 * `seat_occupied`, a conflict, "unassigned from the seat", a retry), and a 422 whose `errors`
 * name nothing (its detail is all there is).
 */
export function submitGeneralError(error: unknown): boolean {
  if (!error) return false;
  if (!submitRefused(error)) return true;
  return ((error as ApiError).problem?.errors ?? []).length === 0;
}

const WHO: Record<string, string> = {
  manager: "a manager",
  owner: "an owner",
  finance: "finance",
  assignee: "the seat holder",
};

/** "a manager or the seat holder" (who fills a missing field). */
export const whoFills = (fill: readonly string[], mine: ReadonlySet<string>) =>
  fill.some((a) => mine.has(a)) ? "you" : fill.map((a) => WHO[a] ?? a).join(" or ");

/** The chapter count line for the rail ("3 of 5 chapters"). */
export const chaptersLine = (view: Pick<FlowRunSummary, "progress">) =>
  `${view.progress.chapters_done} of ${view.progress.chapters_total} chapters`;

export const stepsOf = (def: FlowDef) => def.chapters.flatMap((c) => [...c.steps]);
